import { useEffect, useState } from 'react';

interface FaqHtmlSandboxPreviewProps {
  html?: string | null;
  minHeight?: string;
  className?: string;
  title?: string;
  debounceMs?: number;
}

const FAQ_HTML_PREVIEW_RUNTIME = `<script>
(function () {
  function createMemoryStorage() {
    var store = Object.create(null);
    var api = {
      getItem: function (key) {
        key = String(key);
        return Object.prototype.hasOwnProperty.call(store, key) ? store[key] : null;
      },
      setItem: function (key, value) {
        store[String(key)] = String(value);
      },
      removeItem: function (key) {
        delete store[String(key)];
      },
      clear: function () {
        store = Object.create(null);
      },
      key: function (index) {
        return Object.keys(store)[index] || null;
      }
    };

    Object.defineProperty(api, 'length', {
      get: function () {
        return Object.keys(store).length;
      }
    });

    return api;
  }

  function ensureStorage(name) {
    try {
      var storage = window[name];
      var probeKey = '__faq_html_preview_storage_probe__';
      storage.setItem(probeKey, probeKey);
      storage.removeItem(probeKey);
    } catch (error) {
      try {
        Object.defineProperty(window, name, {
          value: createMemoryStorage(),
          configurable: true
        });
      } catch (defineError) {
      }
    }
  }

  ensureStorage('localStorage');
  ensureStorage('sessionStorage');
})();
<\/script>`;

function injectPreviewRuntime(markup: string) {
  const trimmed = markup.trim();

  if (!trimmed) {
    return '';
  }

  if (/<head(\s[^>]*)?>/i.test(trimmed)) {
    return trimmed.replace(/<head(\s[^>]*)?>/i, (match) => `${match}${FAQ_HTML_PREVIEW_RUNTIME}`);
  }

  if (/<html(\s[^>]*)?>/i.test(trimmed)) {
    return trimmed.replace(/<html(\s[^>]*)?>/i, (match) => `${match}<head>${FAQ_HTML_PREVIEW_RUNTIME}</head>`);
  }

  return `${FAQ_HTML_PREVIEW_RUNTIME}${trimmed}`;
}

export default function FaqHtmlSandboxPreview({
  html,
  minHeight = '420px',
  className = '',
  title = 'HTML 预览',
  debounceMs = 300,
}: FaqHtmlSandboxPreviewProps) {
  const [debouncedHtml, setDebouncedHtml] = useState(html ?? '');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedHtml(html ?? '');
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [html, debounceMs]);

  const hasHtml = debouncedHtml.trim().length > 0;
  const srcDoc = injectPreviewRuntime(debouncedHtml);

  return (
    <div
      className={`overflow-hidden rounded-xl border border-slate-200 bg-white ${className}`}
      style={{ minHeight }}
    >
      {hasHtml ? (
        <iframe
          title={title}
          srcDoc={srcDoc}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          className="block w-full border-0 bg-white"
          style={{ minHeight }}
        />
      ) : (
        <div
          className="flex h-full min-h-[inherit] items-center justify-center px-4 text-center text-sm text-slate-400"
          style={{ minHeight }}
        >
          粘贴 HTML 后会在这里预览
        </div>
      )}
    </div>
  );
}
